import { useMemo, useState, useEffect, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, CalendarDays, X } from "lucide-react";
import type { DateRange as DayPickerDateRange } from "react-day-picker";
import { subMonths } from "date-fns";
import { useGetExpeditionCountWoRefetchBo } from "@/api/expedition.api.tsx";
import { ExpeditionProcessStatusEnum } from "@/constants/enums/expeditionProcessStatus.enum";
import type { GetAllExpeditionBoDto } from "@/types/api/api-types";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  addDays,
  dateRangeToParams,
  dayRange,
  getWeekStart,
  weekLengthForField,
  type DateFilterField,
  type DateFilterType,
} from "@/components/backOffice/shared/dateFilter.util";

const DAYS_FR = ["LUN", "MAR", "MER", "JEU", "VEN", "SAM", "DIM"];

const DEFAULT_COUNT_FILTER: GetAllExpeditionBoDto = {
  processStatus: [ExpeditionProcessStatusEnum.PENDING_PELICAN],
};

function formatDDMM(date: Date): string {
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const NavIconButton = ({ icon, onClick }: { icon: ReactNode; onClick: () => void }) => (
  <button
    onClick={onClick}
    className="flex size-9 items-center justify-center rounded-lg border border-slate-200 bg-white shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)] transition-colors hover:bg-slate-50"
  >
    {icon}
  </button>
);

type DayBlockProps = {
  date: Date;
  dayName: string;
  isSelected: boolean;
  onClick: () => void;
  dateField: DateFilterField;
  countFilter: GetAllExpeditionBoDto;
};

const DayBlock = ({
  date,
  dayName,
  isSelected,
  onClick,
  dateField,
  countFilter,
}: DayBlockProps) => {
  const { data: count } = useGetExpeditionCountWoRefetchBo({
    ...countFilter,
    ...dateRangeToParams(dateField, dayRange(date)),
  });

  const hasData = count != null && count > 0;

  return (
    <div className="flex w-full min-w-0 flex-1 items-center justify-center px-1 md:w-auto">
      <button
        onClick={hasData ? onClick : undefined}
        className={cn(
          "flex h-20 w-full flex-col items-center justify-center gap-2 rounded-lg border p-4 transition-colors",
          isSelected
            ? "cursor-pointer border-[#3562f8] bg-[rgba(53,98,248,0.1)] text-[#3562f8]"
            : hasData
              ? "cursor-pointer border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
              : "cursor-default border-slate-200 bg-slate-50 text-slate-300",
        )}
      >
        <div className="flex items-center gap-1 text-sm font-medium">
          <span>{dayName}</span>
          <span>{date.getDate()}</span>
        </div>
        {hasData ? (
          <div className="flex h-[22px] items-center justify-center rounded-full bg-[#dbe5fe] px-2.5 py-0.5">
            <span className="text-xs font-semibold text-[#3562f8] uppercase">{count}</span>
          </div>
        ) : (
          <span className="text-sm">—</span>
        )}
      </button>
    </div>
  );
};

export type WeekFilterProps = {
  currentWeekStart: Date;
  onWeekStartChange: (weekStart: Date) => void;
  dateAt: [Date, Date] | null;
  dateFilterType: DateFilterType | null;
  onDaySelect: (day: Date | null) => void;
  onWeekSelect: (active: boolean, weekStart: Date) => void;
  onRangeSelect: (range: [Date, Date] | null) => void;
  dateField?: DateFilterField;
  countFilter?: GetAllExpeditionBoDto;
};

export const WeekFilter = ({
  currentWeekStart,
  onWeekStartChange,
  dateAt,
  dateFilterType,
  onDaySelect,
  onWeekSelect,
  onRangeSelect,
  dateField = "pickupDate",
  countFilter = DEFAULT_COUNT_FILTER,
}: WeekFilterProps) => {
  const { t } = useTranslation("boExpeditions");
  const isMobile = useIsMobile();
  const [customOpen, setCustomOpen] = useState(false);
  const [localRange, setLocalRange] = useState<DayPickerDateRange | undefined>(undefined);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (customOpen) {
      setLocalRange(undefined);
      setHasStarted(false);
    }
  }, [customOpen]);

  const weekLength = weekLengthForField(dateField);

  const weekDays = useMemo(
    () => Array.from({ length: weekLength }, (_, i) => addDays(currentWeekStart, i)),
    [currentWeekStart, weekLength],
  );

  const isCurrentWeek = isSameDay(currentWeekStart, getWeekStart(new Date()));

  const selectedDay = dateFilterType === "DAY" ? dateAt?.[0] ?? null : null;
  const isWeekActive = dateFilterType === "WEEK";
  const customDateRange = dateFilterType === "RANGE" ? dateAt : null;

  const handleDayClick = (day: Date) => {
    onDaySelect(selectedDay && isSameDay(selectedDay, day) ? null : day);
  };

  const handleCustomSelect = (range: DayPickerDateRange | undefined) => {
    if (!hasStarted) {
      if (range?.from) {
        setLocalRange({ from: range.from });
        setHasStarted(true);
      }
    } else {
      if (!range?.from) {
        setLocalRange(undefined);
        setHasStarted(false);
        return;
      }
      const start = localRange!.from!;
      const end = range.to ?? range.from;
      const [finalFrom, finalTo] = start <= end ? [start, end] : [end, start];
      onRangeSelect([finalFrom, finalTo]);
      setCustomOpen(false);
    }
  };

  const handleClearCustomRange = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRangeSelect(null);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap justify-between gap-2">
        <div className="flex items-center gap-3">
          <CalendarDays className="size-4 shrink-0" />
          <span className="font-jakarta-sans text-sm font-semibold tracking-wide text-slate-900 uppercase">
            {dateField === "pickupDate"
              ? t("weekFilter.pickupCalendarTitle")
              : t("weekFilter.finalizationCalendarTitle")}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <NavIconButton
            icon={<ArrowLeft className="size-4 text-gray-600" />}
            onClick={() => onWeekStartChange(addDays(currentWeekStart, -7))}
          />
          <button
            onClick={() => onWeekSelect(!isWeekActive, currentWeekStart)}
            className={cn(
              "flex h-9 items-center justify-center gap-2 rounded-lg border px-4 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)] transition-colors",
              isWeekActive
                ? "border-[#3562f8] bg-[rgba(53,98,248,0.1)] text-[#3562f8]"
                : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50",
            )}
          >
            <span className="text-sm font-medium whitespace-nowrap">
              {isCurrentWeek
                ? t("weekFilter.currentWeek")
                : t("weekFilter.weekOf", { date: formatDDMM(currentWeekStart) })}
            </span>
            {isWeekActive && <X className="h-4 w-4" />}
          </button>
          <NavIconButton
            icon={<ArrowRight className="size-4 text-gray-600" />}
            onClick={() => onWeekStartChange(addDays(currentWeekStart, 7))}
          />
          <Popover open={customOpen} onOpenChange={setCustomOpen}>
            <PopoverTrigger asChild>
              <button
                className={cn(
                  "flex h-9 items-center justify-center rounded-lg border shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)] transition-colors",
                  customDateRange
                    ? "gap-2 border-[#3562f8] bg-[rgba(53,98,248,0.1)] px-3 text-[#3562f8]"
                    : "size-9 border-slate-200 bg-white hover:bg-slate-50",
                )}
              >
                {customDateRange ? (
                  <>
                    <span className="text-sm font-medium whitespace-nowrap">
                      {formatDDMM(customDateRange[0])} → {formatDDMM(customDateRange[1])}
                    </span>
                    <span role="button" onClick={handleClearCustomRange} className="shrink-0">
                      <X className="h-4 w-4" />
                    </span>
                  </>
                ) : (
                  <CalendarDays className="size-4" />
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                selected={localRange}
                onSelect={handleCustomSelect}
                numberOfMonths={isMobile ? 1 : 2}
                defaultMonth={isMobile ? undefined : subMonths(new Date(), 1)}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <div className="flex flex-col items-start gap-1 md:flex-row">
        {weekDays.map((day, i) => (
          <DayBlock
            key={day.toISOString()}
            date={day}
            dayName={DAYS_FR[i]}
            isSelected={!!selectedDay && isSameDay(selectedDay, day)}
            onClick={() => handleDayClick(day)}
            dateField={dateField}
            countFilter={countFilter}
          />
        ))}
      </div>
    </div>
  );
};
